import React, { useEffect } from 'react';
import { useTopicStore } from './store';
import useHorizontalPointerDrag from './hooks/useHorizontalPointerDrag';
import ThinkingIndicator from '../../components/ThinkingIndicator';

interface LocalFoodV2Props {
  topicId?: string | null;
  userLanguageLabel: string;
  viewportWikiLanguage: string;
  isGuestAccount?: boolean;
  onSignInClick?: () => void;
}

export default function LocalFoodV2({
  topicId,
  userLanguageLabel,
  viewportWikiLanguage,
  isGuestAccount = true,
  onSignInClick = () => {},
}: LocalFoodV2Props) {
  const fetchLocalFoods = useTopicStore((s) => s.fetchLocalFoods);
  const localFoodsState = useTopicStore((s) => {
    if (!topicId) return null;
    const t = s.topics[topicId];
    if (!t || t.type !== 'location') return null;
    return t.localFoods ?? null;
  });

  useEffect(() => {
    if (!topicId) return;
    if (localFoodsState?.isLoading) return;
    if (
      localFoodsState?.items?.length &&
      localFoodsState?.languageLabel === userLanguageLabel &&
      !localFoodsState?.error
    ) {
      return;
    }

    void fetchLocalFoods(topicId, { userLanguageLabel, viewportWikiLanguage });
  }, [
    topicId,
    userLanguageLabel,
    viewportWikiLanguage,
    localFoodsState?.isLoading,
    localFoodsState?.items?.length,
    localFoodsState?.languageLabel,
    localFoodsState?.error,
    fetchLocalFoods,
  ]);

  const items = localFoodsState?.items ?? [];
  const isLoading = !!localFoodsState?.isLoading;
  const error = localFoodsState?.error ?? null;
  const foodSliderRef = useHorizontalPointerDrag<HTMLDivElement>();

  return (
    <div className="food-section">
      <h3 className="section-heading">Local Food</h3>

      {isLoading && (
        <ThinkingIndicator />
      )}

      {error &&
        (error === 'AUTH_REQUIRED' && isGuestAccount ? (
          <div style={{ padding: '15px 10px', textAlign: 'center', margin: '10px 0' }}>
            <button
              onClick={onSignInClick}
              style={{
                backgroundColor: '#22c55e',
                color: 'white',
                border: 'none',
                padding: '12px 20px',
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: 'pointer',
                width: '100%',
              }}
            >
              Sign in to learn more
            </button>
          </div>
        ) : (
          <div style={{ color: '#666', padding: '10px' }}>{error}</div>
        ))}

      {items.length > 0 && !error && (
        <div ref={foodSliderRef} className="food-slider slider-horizontal">
          {items.map((dish, i) => (
            <div key={`${dish.title}-${i}`} className="food-card slider-card">
              <div className="food-card-thumbnail-container slider-card-thumbnail-container">
                <img src={dish.imagelink} alt={dish.title} className="food-thumb slider-card-image" />
                <div className="food-card-title-overlay slider-card-title-overlay">
                  <strong>{dish.title}</strong>
                </div>
              </div>
              <div className="food-card-description slider-card-description">
                <p>{dish.description}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
